import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment-timezone";

const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
import Button from "@/components/common/Button";
import FormField from "@/components/common/FormField";
import { MainHeading } from "@/components/common/MainHeading";
import { Layout } from "@/components/Layout/Layout";
import { callApi, ThrowError } from "@/api/callApi";
import { API_ENDPOINT, API_URL } from "@/api/constant";
import { showToastNotification } from "@/utility/helper";
import { errorTypes } from "@/utility/validation";
import { IAddCouponForm, ISingleCouponResponse } from "@/interfaces/Coupon/coupon.interface";
import { ICategory, ICategoryResponse } from "@/interfaces/Category/category.interface";
import { MultiSelect } from "@/components/common/MultiSelect";

export const AddCoupon = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const isEditMode = Boolean(id);
    const [coupanId, setCoupanId] = useState<string>("");
    const [categories, setCategories] = useState<ICategory[]>([]);
    const [loadingCategories, setLoadingCategories] = useState(false);
    const [loadingCoupon, setLoadingCoupon] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    const fetchCategories = async (query = "") => {
        setLoadingCategories(true);
        try {
            const response = await callApi<ICategoryResponse>(
                `${API_URL + API_ENDPOINT.categories}?limit=100&search=${query}`,
                { method: 'GET' }
            );

            const newCategories = response?.data?.categories || [];
            setCategories(prev => {
                const selectedIds = watch('categoryIds') || [];
                const selectedOnes = prev.filter(c => selectedIds.includes(c.id));
                const combined = [...newCategories];

                selectedOnes.forEach(s => {
                    if (!combined.find(c => c.id === s.id)) {
                        combined.push(s);
                    }
                });

                return combined;
            });
        } catch (error) {
            console.error("Error fetching categories:", error);
        } finally {
            setLoadingCategories(false);
        }
    };

    const {
        register,
        control,
        handleSubmit,
        reset,
        watch,
        setValue,
        formState: { errors, isSubmitting },
    } = useForm<IAddCouponForm>({
        defaultValues: {
            type: 'percentage',
            status: 'active',
            categoryIds: [],
            minimumValue: 0,
            discountValue: 0,
            maxDiscount: null,
            maxTotalUsage: null,
            maxUsagePerUser: 1,
        }
    });

    // Initial fetch
    useEffect(() => {
        fetchCategories();
    }, []);

    // Debounced search fetch
    useEffect(() => {
        if (!searchTerm) {
            fetchCategories();
            return;
        }

        const timer = setTimeout(() => {
            fetchCategories(searchTerm);
        }, 300);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        if (isEditMode && id) {
            const fetchCouponDetails = async () => {
                setLoadingCoupon(true);
                try {
                    const response = await callApi<ISingleCouponResponse>(
                        `${API_URL + API_ENDPOINT.coupons}/${id}`,
                        { method: 'GET' }
                    );
                    const coupon = response?.data;
                    setCoupanId(coupon?.id);
                    reset({
                        name: coupon.name,
                        code: coupon.code,
                        type: coupon.type,
                        description: coupon.description,
                        discountValue: coupon.discountValue,
                        maxDiscount: coupon.maxDiscount,
                        startDate: moment(coupon.startDate).format('YYYY-MM-DD'),
                        expirationDate: moment(coupon.expirationDate).format('YYYY-MM-DD'),
                        minimumValue: coupon.minimumValue,
                        maxTotalUsage: coupon.maxTotalUsage,
                        maxUsagePerUser: coupon.maxUsagePerUser,
                        maxUsagePerDay: coupon.maxUsagePerDay,
                        maxUsagePerUserPerDay: coupon.maxUsagePerUserPerDay,
                        status: coupon.status,
                        categoryIds: coupon.categoryIds,
                    });
                } catch (error) {
                    ThrowError(error);
                } finally {
                    setLoadingCoupon(false);
                }
            };
            fetchCouponDetails();
        }
    }, [id, isEditMode, reset]);

    const onSubmit = async (data: IAddCouponForm) => {
        try {
            const payload = {
                ...data,
                name: data.name?.trim(),
                description: data.description?.trim(),
                discountValue: Number(data.discountValue),
                maxDiscount: data.maxDiscount ? Number(data.maxDiscount) : null,
                minimumValue: Number(data.minimumValue),
                maxTotalUsage: data.maxTotalUsage !== null ? Number(data.maxTotalUsage) : null,
                maxUsagePerUser: data.maxUsagePerUser !== null ? Number(data.maxUsagePerUser) : null,
                maxUsagePerDay: data.maxUsagePerDay !== null ? Number(data.maxUsagePerDay) : null,
                maxUsagePerUserPerDay: data.maxUsagePerUserPerDay !== null ? Number(data.maxUsagePerUserPerDay) : null,
                code: data.code?.trim().toUpperCase(),
                startDate: moment.tz(data.startDate, userTimezone)
                    .startOf("day")
                    .utc()
                    .toISOString(),

                expirationDate: moment.tz(data.expirationDate, userTimezone)
                    .endOf("day")
                    .utc()
                    .toISOString(),
            };

            if (isEditMode) {
                delete payload.code;
            }
            const url = isEditMode
                ? `${API_URL + API_ENDPOINT.coupons}/${coupanId}`
                : `${API_URL + API_ENDPOINT.coupons}`;

            const method = isEditMode ? 'PUT' : 'POST';

            const response = await callApi(url, {
                method,
                data: payload
            });

            showToastNotification('success', response?.message || `Coupon ${isEditMode ? 'updated' : 'created'} successfully`);
            navigate(-1);
        } catch (error) {
            ThrowError(error);
        }
    };

    if (loadingCoupon) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-[400px]">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
            </Layout>
        );
    }

    console.log("watch('startDate')", watch('startDate'));

    return (
        <Layout>
            <MainHeading
                title={isEditMode ? "Edit Coupon" : "Add Coupon"}
                showBack
                onBack={() => navigate(-1)}
            />
            <div className="bg-card rounded-lg border p-6 shadow-sm mb-6 ">
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
                    {/* ── Basic Information ── */}
                    <div className="space-y-4">
                        <h3 className="text-sm font-bold text-primary border-b pb-2">Basic Information</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                            <FormField
                                label="Campaign Name"
                                name="name"
                                placeholder="e.g. Winter Sale 2025"
                                requiredIcon
                                error={errors.name?.message}
                                {...register('name', { ...errorTypes.required('Name') })}
                            />
                            <FormField
                                label="Coupon Code"
                                name="code"
                                placeholder="SAVE20"
                                requiredIcon
                                disabled={isEditMode}
                                inputClass={`uppercase tracking-widest font-bold w-full h-10 px-3 py-2 rounded-md border bg-background text-sm ${isEditMode ? 'opacity-60 cursor-not-allowed' : ''}`}
                                error={errors.code?.message}
                                {...register('code', { ...errorTypes.required('Code') })}
                            />
                            <div className="md:col-span-2">
                                <FormField
                                    label="Description"
                                    name="description"
                                    type="textarea"
                                    rows={3}
                                    placeholder="Detailed description of the coupon campaign..."
                                    {...register('description')}
                                />
                            </div>
                        </div>
                    </div>

                    {/* ── Validity Period ── */}
                    <div className="space-y-4">
                        <h3 className="text-sm font-bold text-primary border-b pb-2">Validity Period</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                            <FormField
                                label="Start Date"
                                name="startDate"
                                type="date"
                                requiredIcon
                                min={moment().format('YYYY-MM-DD')}
                                error={errors.startDate?.message}
                                {...register('startDate', { ...errorTypes.required('Required') })}
                            />
                            <FormField
                                label="Expiry Date"
                                name="expirationDate"
                                type="date"
                                requiredIcon
                                min={watch('startDate') || moment().format('YYYY-MM-DD')}
                                error={errors.expirationDate?.message}
                                {...register('expirationDate', {
                                    ...errorTypes.required('Required'),
                                    validate: (value) => {
                                        const startDate = watch('startDate');
                                        if (startDate && moment(value).isBefore(moment(startDate))) {
                                            return 'Expiry Date cannot be before Start Date';
                                        }
                                        return true;
                                    }
                                })}
                            />
                        </div>
                    </div>

                    {/* ── Discount Value ── */}
                    <div className="space-y-4">
                        <h3 className="text-sm font-bold text-primary border-b pb-2">Discount Value</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                            <Controller
                                name="type"
                                control={control}
                                render={({ field }) => (
                                    <FormField
                                        label="Discount Type"
                                        type="select"
                                        options={[
                                            { label: 'Percentage (%)', value: 'percentage' },
                                            { label: 'Flat Rate', value: 'flat' }
                                        ]}
                                        value={field.value}
                                        onValueChange={field.onChange}
                                    />
                                )}
                            />
                            <FormField
                                label="Value"
                                name="discountValue"
                                type="number"
                                requiredIcon
                                min={0}
                                suffix={watch('type') === 'percentage' ? '%' : 'R'}
                                error={errors.discountValue?.message}
                                {...register('discountValue', { ...errorTypes.required('Value') })}
                            />
                            <FormField
                                label="Max Discount"
                                name="maxDiscount"
                                type="number"
                                placeholder="0"
                                min={0}
                                error={errors.maxDiscount?.message}
                                {...register('maxDiscount')}
                            />
                            <FormField
                                label="Min Purchase Value"
                                name="minimumValue"
                                type="number"
                                placeholder="0.00"
                                prefix="R"
                                min={0}
                                error={errors.minimumValue?.message}
                                {...register('minimumValue')}
                            />
                        </div>
                    </div>

                    {/* ── Usage Limits ── */}
                    <div className="space-y-4">
                        <h3 className="text-sm font-bold text-primary border-b pb-2">Usage Limits</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
                            {[
                                { name: 'maxTotalUsage', label: 'Max Total Usage' },
                                { name: 'maxUsagePerUser', label: 'Max Usage Per User' },
                                { name: 'maxUsagePerDay', label: 'Max Usage Per Day' },
                                { name: 'maxUsagePerUserPerDay', label: 'Max Usage Per User Per Day' },
                            ].map((field) => (
                                <div key={field.name} className="space-y-2">
                                    <FormField
                                        label={field.label}
                                        name={field.name}
                                        type="number"
                                        disabled={watch(field.name as any) === null}
                                        placeholder="1"
                                        wrapperClass="mb-0"
                                        min={0}
                                        {...register(field.name as any)}
                                    />
                                    <label className="flex items-center gap-2 text-xs cursor-pointer text-muted-foreground hover:text-foreground transition-colors w-fit">
                                        <input
                                            type="checkbox"
                                            className="rounded border-gray-300 accent-primary"
                                            checked={watch(field.name as any) === null}
                                            onChange={(e) => {
                                                setValue(field.name as any, e.target.checked ? null : 1);
                                            }}
                                        />
                                        Unlimited
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* ── Applicability ── */}
                    <div className="space-y-4">
                        <h3 className="text-sm font-bold text-primary border-b pb-2">Applicability</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                            <div className="md:col-span-2">
                                <div className="flex flex-col gap-1.5 grow">
                                    <label className="text-xs font-bold text-foreground">
                                        Select Target Categories
                                    </label>
                                    <Controller
                                        name="categoryIds"
                                        control={control}
                                        render={({ field }) => (
                                            <MultiSelect
                                                options={categories.map(cat => ({ label: cat.name, value: cat.id }))}
                                                selected={field.value || []}
                                                onChange={field.onChange}
                                                onSearchChange={setSearchTerm}
                                                placeholder="Search and select categories..."
                                                loading={loadingCategories}
                                            />
                                        )}
                                    />
                                    <p className="text-[10px] text-muted-foreground mt-1">
                                        Leave empty to apply this coupon to all categories.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ── Actions ── */}
                    <div className="flex justify-end gap-3 pt-8 border-t">
                        <Button
                            type="button"
                            variant="outline"
                            className="px-8 h-11"
                            onClick={() => navigate(-1)}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            className="px-10 h-11 shadow-lg shadow-primary/20"
                            isLoading={isSubmitting}
                            loadingText={isEditMode ? "Updating..." : "Creating..."}
                        >
                            {isEditMode ? "Update Coupon" : "Create Coupon"}
                        </Button>
                    </div>
                </form>
            </div>
        </Layout>
    );
};
